import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GroupIcon from "@mui/icons-material/Group";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ChartTooltip,
  Legend,
  Filler
);

// --- Utility Functions ---

function parseNumber(val) {
  if (val == null || val === "") return NaN;
  const str = String(val).replace(/[^0-9.\-]/g, "");
  return parseFloat(str);
}

function isNumericValue(val) {
  return !isNaN(parseNumber(val));
}

function parseYear(val) {
  if (val == null || val === "") return null;
  const str = String(val).trim();

  // Try YYYY-MM-DD or YYYY/MM/DD
  let m = str.match(/^(\d{4})[-/]\d{1,2}[-/]\d{1,2}/);
  if (m) return parseInt(m[1], 10);

  // Try DD/MM/YYYY or DD-MM-YYYY or MM/DD/YYYY
  m = str.match(/\d{1,2}[-/]\d{1,2}[-/](\d{4})/);
  if (m) return parseInt(m[1], 10);

  // Try DD-Mon-YYYY (e.g., 15-Jan-2024)
  m = str.match(/\d{1,2}-[A-Za-z]{3}-(\d{4})/);
  if (m) return parseInt(m[1], 10);

  // Try DD-Mon-YY (e.g., 15-Jan-24)
  m = str.match(/\d{1,2}-[A-Za-z]{3}-(\d{2})$/);
  if (m) {
    const yy = parseInt(m[1], 10);
    return yy > 50 ? 1900 + yy : 2000 + yy;
  }

  // Try serial number (Excel date serial)
  const num = parseFloat(str);
  if (!isNaN(num) && num > 30000 && num < 60000) {
    const excelEpoch = new Date(1899, 11, 30);
    const d = new Date(excelEpoch.getTime() + num * 86400000);
    return d.getFullYear();
  }

  return null;
}

function detectColumnTypes(headers, rows) {
  const sampleSize = Math.min(rows.length, 30);
  const types = {};

  headers.forEach((header, idx) => {
    let numericCount = 0;
    let dateCount = 0;
    let nonEmptyCount = 0;

    for (let i = 0; i < sampleSize; i++) {
      const cell = rows[i]?.cells?.[idx];
      const val = cell?.value;
      if (val == null || String(val).trim() === "") continue;
      nonEmptyCount++;
      if (isNumericValue(val)) numericCount++;
      if (parseYear(val) !== null) dateCount++;
    }

    if (nonEmptyCount === 0) {
      types[header] = "text";
    } else if (numericCount / nonEmptyCount > 0.6) {
      types[header] = "numeric";
    } else if (dateCount / nonEmptyCount > 0.6) {
      types[header] = "date";
    } else {
      types[header] = "text";
    }
  });

  return types;
}

const CHART_COLORS = [
  "#3498db", "#e74c3c", "#2ecc71", "#f39c12", "#9b59b6",
  "#1abc9c", "#e67e22", "#34495e", "#16a085", "#c0392b",
  "#27ae60", "#2980b9", "#8e44ad", "#d35400", "#7f8c8d",
];

function formatAmount(num) {
  if (num == null || isNaN(num)) return "0";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

const AUTHORIZED_USERS = ['faiz@4sight-global.com', 'ram@4sight-global.com'];

// --- Main Component ---

export default function AccountsReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amountCol, setAmountCol] = useState("");
  const [groupByCol, setGroupByCol] = useState("");
  const [dateCol, setDateCol] = useState("");

  const currentUser = (localStorage.getItem("username") || "").toLowerCase();
  const isAuthorized = AUTHORIZED_USERS.includes(currentUser);

  useEffect(() => {
    if (!isAuthorized) navigate("/accounts", { replace: true });
  }, [isAuthorized, navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("onedrive/api/data/");
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Detect column types once data loads
  const columnTypes = useMemo(() => {
    if (!data?.headers || !data?.rows) return {};
    return detectColumnTypes(data.headers, data.rows);
  }, [data]);

  const numericCols = useMemo(
    () => Object.entries(columnTypes).filter(([, t]) => t === "numeric").map(([h]) => h),
    [columnTypes]
  );
  const dateCols = useMemo(
    () => Object.entries(columnTypes).filter(([, t]) => t === "date").map(([h]) => h),
    [columnTypes]
  );
  const textCols = useMemo(
    () => Object.entries(columnTypes).filter(([, t]) => t === "text").map(([h]) => h),
    [columnTypes]
  );

  // Auto-select defaults when data loads
  useEffect(() => {
    if (numericCols.length > 0 && !amountCol) setAmountCol(numericCols[0]);
    if (textCols.length > 0 && !groupByCol) setGroupByCol(textCols[0]);
    if (dateCols.length > 0 && !dateCol) setDateCol(dateCols[0]);
  }, [numericCols, textCols, dateCols, amountCol, groupByCol, dateCol]);

  // Build index maps for selected columns
  const amountIdx = data?.headers?.indexOf(amountCol) ?? -1;
  const groupIdx = data?.headers?.indexOf(groupByCol) ?? -1;
  const dateIdx = data?.headers?.indexOf(dateCol) ?? -1;

  // --- Computed Aggregations ---

  const { groupedData, totalAmount, rowCount } = useMemo(() => {
    if (!data?.rows || amountIdx < 0)
      return { groupedData: {}, totalAmount: 0, rowCount: 0 };

    const grouped = {};
    let total = 0;
    let count = 0;

    for (const row of data.rows) {
      const cells = row.cells || [];
      const amount = parseNumber(cells[amountIdx]?.value);
      if (isNaN(amount)) continue;

      const groupVal =
        groupIdx >= 0
          ? String(cells[groupIdx]?.value || "").trim() || "(Blank)"
          : "All";

      if (!grouped[groupVal]) grouped[groupVal] = { total: 0, count: 0 };
      grouped[groupVal].total += amount;
      grouped[groupVal].count += 1;
      total += amount;
      count++;
    }

    return { groupedData: grouped, totalAmount: total, rowCount: count };
  }, [data, amountIdx, groupIdx]);

  const yearlyData = useMemo(() => {
    if (!data?.rows || amountIdx < 0 || dateIdx < 0) return {};

    const yearly = {};
    for (const row of data.rows) {
      const cells = row.cells || [];
      const amount = parseNumber(cells[amountIdx]?.value);
      if (isNaN(amount)) continue;

      const year = parseYear(cells[dateIdx]?.value);
      if (!year) continue;

      if (!yearly[year]) yearly[year] = 0;
      yearly[year] += amount;
    }

    return yearly;
  }, [data, amountIdx, dateIdx]);

  // Sort grouped entries by total descending
  const sortedGroups = useMemo(
    () =>
      Object.entries(groupedData)
        .sort(([, a], [, b]) => b.total - a.total),
    [groupedData]
  );

  const sortedYears = useMemo(
    () =>
      Object.entries(yearlyData)
        .sort(([a], [b]) => Number(a) - Number(b)),
    [yearlyData]
  );

  // --- Chart Data ---

  const barChartData = useMemo(() => {
    const top15 = sortedGroups.slice(0, 15);
    return {
      labels: top15.map(([label]) => label.length > 25 ? label.slice(0, 25) + "..." : label),
      datasets: [
        {
          label: amountCol || "Amount",
          data: top15.map(([, d]) => d.total),
          backgroundColor: CHART_COLORS.slice(0, top15.length),
          borderRadius: 4,
        },
      ],
    };
  }, [sortedGroups, amountCol]);

  const lineChartData = useMemo(
    () => ({
      labels: sortedYears.map(([year]) => year),
      datasets: [
        {
          label: amountCol || "Amount",
          data: sortedYears.map(([, val]) => val),
          borderColor: "#3498db",
          backgroundColor: "rgba(52, 152, 219, 0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 5,
          pointBackgroundColor: "#3498db",
        },
      ],
    }),
    [sortedYears, amountCol]
  );

  const doughnutData = useMemo(() => {
    const top8 = sortedGroups.slice(0, 8);
    const othersTotal = sortedGroups
      .slice(8)
      .reduce((sum, [, d]) => sum + d.total, 0);
    const labels = top8.map(([l]) => l);
    const values = top8.map(([, d]) => d.total);
    if (othersTotal > 0) {
      labels.push("Others");
      values.push(othersTotal);
    }
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: CHART_COLORS.slice(0, labels.length),
          borderWidth: 2,
          borderColor: "#fff",
        },
      ],
    };
  }, [sortedGroups]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatAmount(ctx.raw)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (val) => formatAmount(val),
        },
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: { display: true },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right", labels: { boxWidth: 12, padding: 12 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const pct = ((ctx.raw / totalAmount) * 100).toFixed(1);
            return `${ctx.label}: ${formatAmount(ctx.raw)} (${pct}%)`;
          },
        },
      },
    },
  };

  // --- Render ---

  if (!isAuthorized) return null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchData}>RETRY</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data?.rows?.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Upload an accounts Excel file first.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Tooltip title="Back to Accounts">
            <IconButton onClick={() => navigate("/accounts")} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Accounts Report
          </Typography>
          {data.file_name && (
            <Chip label={data.file_name} size="small" variant="outlined" />
          )}
        </Stack>
        <Tooltip title="Refresh data">
          <IconButton onClick={fetchData} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Column Selectors */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Amount Column</InputLabel>
            <Select value={amountCol} label="Amount Column" onChange={(e) => setAmountCol(e.target.value)}>
              {numericCols.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Group By</InputLabel>
            <Select value={groupByCol} label="Group By" onChange={(e) => setGroupByCol(e.target.value)}>
              {textCols.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Date Column (for trends)</InputLabel>
            <Select value={dateCol} label="Date Column (for trends)" onChange={(e) => setDateCol(e.target.value)}>
              {dateCols.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
              {dateCols.length === 0 && (
                <MenuItem value="" disabled>No date columns detected</MenuItem>
              )}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Summary Cards */}
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1, bgcolor: "#eef2ff", border: "1px solid #c7d2fe" }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, "&:last-child": { pb: 2 } }}>
            <AttachMoneyIcon sx={{ fontSize: 40, color: "#4f46e5" }} />
            <Box>
              <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#4f46e5" }}>
                {formatAmount(totalAmount)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, bgcolor: "#ecfdf5", border: "1px solid #a7f3d0" }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, "&:last-child": { pb: 2 } }}>
            <ReceiptIcon sx={{ fontSize: 40, color: "#059669" }} />
            <Box>
              <Typography variant="body2" color="text.secondary">Total Invoices</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#059669" }}>
                {rowCount}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, "&:last-child": { pb: 2 } }}>
            <GroupIcon sx={{ fontSize: 40, color: "#b45309" }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Unique {groupByCol || "Groups"}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#b45309" }}>
                {sortedGroups.length}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2, "&:last-child": { pb: 2 } }}>
            <TrendingUpIcon sx={{ fontSize: 40, color: "#dc2626" }} />
            <Box>
              <Typography variant="body2" color="text.secondary">Years Covered</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#dc2626" }}>
                {sortedYears.length}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* Charts Row */}
      <Stack direction={{ xs: "column", lg: "row" }} gap={2} sx={{ mb: 3 }}>
        {/* Bar Chart */}
        <Paper variant="outlined" sx={{ flex: 2, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {amountCol} by {groupByCol}
          </Typography>
          <Box sx={{ height: 350 }}>
            {sortedGroups.length > 0 ? (
              <Bar data={barChartData} options={chartOptions} />
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: "center", pt: 10 }}>
                No data to display
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Doughnut Chart */}
        <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Distribution by {groupByCol}
          </Typography>
          <Box sx={{ height: 350 }}>
            {sortedGroups.length > 0 ? (
              <Doughnut data={doughnutData} options={doughnutOptions} />
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: "center", pt: 10 }}>
                No data to display
              </Typography>
            )}
          </Box>
        </Paper>
      </Stack>

      {/* Yearly Trend */}
      {sortedYears.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Yearly Trend ({dateCol})
          </Typography>
          <Box sx={{ height: 300 }}>
            <Line data={lineChartData} options={lineChartOptions} />
          </Box>
        </Paper>
      )}

      {/* Grouped Table */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2, pb: 1 }}>
          Breakdown by {groupByCol}
        </Typography>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{groupByCol}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total {amountCol}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Count</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">% Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedGroups.map(([label, d], idx) => (
                <TableRow key={label} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">{formatAmount(d.total)}</TableCell>
                  <TableCell align="right">{d.count}</TableCell>
                  <TableCell align="right">
                    {totalAmount > 0 ? ((d.total / totalAmount) * 100).toFixed(1) + "%" : "0%"}
                  </TableCell>
                </TableRow>
              ))}
              {sortedGroups.length > 0 && (
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(totalAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{rowCount}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Yearly Breakdown Table */}
      {sortedYears.length > 0 && (
        <Paper variant="outlined">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2, pb: 1 }}>
            Year-wise Breakdown ({dateCol})
          </Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total {amountCol}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">% Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedYears.map(([year, val]) => (
                  <TableRow key={year} hover>
                    <TableCell>{year}</TableCell>
                    <TableCell align="right">{formatAmount(val)}</TableCell>
                    <TableCell align="right">
                      {totalAmount > 0 ? ((val / totalAmount) * 100).toFixed(1) + "%" : "0%"}
                    </TableCell>
                  </TableRow>
                ))}
                {sortedYears.length > 0 && (
                  <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(sortedYears.reduce((s, [, v]) => s + v, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
